/**
 * Wall 2D (plan-cut) contribution. The shared plan pipeline unions cut outlines; this
 * module supplies EVERY interior layer-boundary line for one wall (Matt's audit: the
 * 2D poché must show all assembly layers, not just the two core faces — finish and
 * substrate were lumping together with no line between them). Layer lines are per-wall,
 * not unioned: at junctions they may run into a neighbor's footprint (Revit-style
 * pre-cleanup look; refinement backlog).
 */
import {
  centerlineOffsetFt, offsetPolyline, pathOps, flattenPathPts, pathArcs,
  type Path2D, type Pt2, type ResolvedElement,
} from '@aj/bim-kernel'
import { differenceClosed } from '../../clipper'
import type { ArcCircle } from '../../arcs2d'
import { endWrapSpec, trimPolylineEnds, type WrapEnds } from './endWrap'

export interface WallPlanContribution {
  elementId: string
  /** open polylines for every interior layer boundary, already offset from the path
   *  (CURVES round: arc edges arrive TESSELLATED — the true-arc channel re-fits from
   *  `coreCircles` after clipping/mitering) */
  coreLines: Pt2[][]
  /**
   * P5e: each line's cumulative depth from the EXTERIOR face (parallel to coreLines)
   * — the corner-miter matcher pairs boundaries of equal depth (or mirrored depth,
   * t−d, when the neighbor's orientation opposes) and butts the rest.
   */
  depths: number[]
  /** assembly thickness — the mirrored-depth comparison needs it */
  thicknessFt: number
  /** CURVES round: each line's source circles (parallel to coreLines; empty for
   *  straight walls) — the true-arc refit candidates */
  coreCircles: ArcCircle[][]
}

export function wallCoreLines(el: ResolvedElement, wrap?: WrapEnds | null): WallPlanContribution | null {
  const stored = el.params.path as Path2D | null
  if (!stored || stored.nodes.length < 2 || !el.assembly) return null
  const t = el.assemblyThicknessFt
  if (t <= 0) return null
  // 2.6a: face offsets are relative to the CENTERLINE — derive it from the stored
  // reference path first (identity when locationLine = wallCenterline). A chain drawn
  // back to its start converts to a true closed ring first (2.6 polish) so the layer
  // lines miter at the seam corner like generate3d's body does.
  const s0 = stored.nodes[0].pt, sL = stored.nodes[stored.nodes.length - 1].pt
  const isLoop = !stored.closed && stored.nodes.length >= 4 &&
    Math.hypot(s0[0] - sL[0], s0[1] - sL[1]) < 1e-6
  const base: Path2D = isLoop ? { closed: true, nodes: stored.nodes.slice(0, -1) } : stored
  const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
  const path = delta === 0 ? base : offsetPolyline(base, delta)
  if (!path) return null
  const layers = el.assembly.layers
  const flipped = el.params.flipped === true
  const sign = flipped ? -1 : 1
  // EVERY interior layer boundary: cumulative thickness from the exterior face at each
  // layer break (skip 0 and t — they coincide with the outline). layers run
  // exterior→interior; the exterior face sits at +t/2 along the LEFT normal of travel.
  const coreLines: Pt2[][] = []
  const depths: number[] = []
  const coreCircles: ArcCircle[][] = []
  // WALL END WRAP (endWrap.ts): at a free end the inner boundaries stop one board short,
  // and a return line closes across between the two face boards
  const ws = wrap && (wrap.start || wrap.end) && !path.closed ? endWrapSpec(el) : null
  const wrapEnds: Pt2[][] = [[], []]   // [start, end] → the trimmed face-board boundary points
  for (let i = 0, cum = 0; i < layers.length - 1; i++) {
    cum += layers[i].thicknessFt
    if (cum <= 1e-9 || cum >= t - 1e-9) continue
    const off = sign * (t / 2 - cum)
    const moved = pathOps.offsetAllEdges(path, off)
    if (moved) {
      // CURVES round: flatten (arc edges tessellate; identical to the bare node
      // points for straight walls). flattenPathPts already appends a closed ring's
      // closing point, so no manual close is needed in either case.
      let pts = flattenPathPts(moved)
      if (ws) {
        const cut = trimPolylineEnds(pts, wrap!.start, wrap!.end, ws.w)
        if (cut) {
          pts = cut
          if (Math.abs(cum - ws.d0) < 1e-6 || Math.abs(cum - ws.d1) < 1e-6) {
            wrapEnds[0].push(pts[0]); wrapEnds[1].push(pts[pts.length - 1])
          }
        }
      }
      coreLines.push(pts)
      depths.push(cum)
      coreCircles.push(pathArcs(moved).map(({ arc }) => ({ c: arc.c, r: arc.r })))
    }
  }
  if (ws) {
    for (const [k, on] of [[0, wrap!.start], [1, wrap!.end]] as const) {
      if (!on || wrapEnds[k].length !== 2) continue
      coreLines.push([wrapEnds[k][0], wrapEnds[k][1]])
      depths.push(ws.d0)
      coreCircles.push([])
    }
  }
  return coreLines.length ? { elementId: el.id, coreLines, depths, thicknessFt: t, coreCircles } : null
}

/**
 * CURVES round: the wall's two FACE circles per arc edge — the refit candidates for
 * the plan's boolean-derived heavy outline (the union facets lie on these circles
 * wherever an arc face reached the union un-reshaped). Empty for straight walls.
 */
export function wallFaceCircles(el: ResolvedElement): ArcCircle[] {
  const stored = el.params.path as Path2D | null
  if (!stored || stored.nodes.length < 2 || !el.assembly) return []
  if (!stored.nodes.some((n) => Math.abs(n.bulge ?? 0) > 1e-12)) return []
  const t = el.assemblyThicknessFt
  if (t <= 0) return []
  const s0 = stored.nodes[0].pt, sL = stored.nodes[stored.nodes.length - 1].pt
  const isLoop = !stored.closed && stored.nodes.length >= 4 &&
    Math.hypot(s0[0] - sL[0], s0[1] - sL[1]) < 1e-6
  const base: Path2D = isLoop ? { closed: true, nodes: stored.nodes.slice(0, -1) } : stored
  const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
  const centerline = delta === 0 ? base : offsetPolyline(base, delta)
  if (!centerline) return []
  const out: ArcCircle[] = []
  for (const off of [t / 2, -t / 2]) {
    const face = pathOps.offsetAllEdges(centerline, off)
    if (face) for (const { arc } of pathArcs(face)) out.push({ c: arc.c, r: arc.r })
  }
  return out
}

export interface WallLayerBand {
  /** closed ring(s) of this layer's plan footprint (annulus rings for loop walls) */
  rings: Pt2[][]
  /** the band's MID-DEPTH polyline — oriented patterns (batt) run along it */
  mid: Pt2[]
  materialId: string
  /** cumulative depth range from the EXTERIOR face */
  depth0: number
  depth1: number
  /** WALL CORE DEFINITION round: this layer sits inside the assembly's coreBoundary —
   *  the band the instance `insulation` toggle draws its batt hatch over */
  isCore: boolean
  /** SCOPE ON THE ELEMENT: the assembly layer this band is (scope part `layer:<i>`) */
  layerIndex: number
}

/**
 * P6b (docs/bim-plan/08 Part 1): per-LAYER closed band regions — the poché fill
 * geometry. Same centerline/offset math as wallCoreLines (2.6a location lines,
 * loop-aware); an open run's band stitches the two boundary offsets into one ring, a
 * closed loop's band is the outer-minus-inner annulus (clipper difference, which may
 * legitimately return several rings).
 */
export function wallLayerBands(el: ResolvedElement, wrap?: WrapEnds | null): WallLayerBand[] | null {
  const stored = el.params.path as Path2D | null
  if (!stored || stored.nodes.length < 2 || !el.assembly) return null
  const t = el.assemblyThicknessFt
  if (t <= 0) return null
  const s0 = stored.nodes[0].pt, sL = stored.nodes[stored.nodes.length - 1].pt
  const isLoop = !stored.closed && stored.nodes.length >= 4 &&
    Math.hypot(s0[0] - sL[0], s0[1] - sL[1]) < 1e-6
  const base: Path2D = isLoop ? { closed: true, nodes: stored.nodes.slice(0, -1) } : stored
  const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
  const path = delta === 0 ? base : offsetPolyline(base, delta)
  if (!path) return null
  const sign = el.params.flipped === true ? -1 : 1
  // CURVES round: band boundaries flatten their arc edges (poché fills are boolean
  // products and stay facets by design). flattenPathPts appends a closed ring's
  // closing point — trimmed back off here since clipper wants unclosed rings.
  const boundaryAt = (depth: number): Pt2[] | null => {
    const off = sign * (t / 2 - depth)
    const moved = Math.abs(off) < 1e-9 ? path : pathOps.offsetAllEdges(path, off)
    if (!moved) return null
    const pts = flattenPathPts(moved)
    if (path.closed && pts.length > 1) pts.pop()
    return pts
  }
  const out: WallLayerBand[] = []
  // WALL END WRAP (endWrap.ts): the layers between the two face boards stop one board
  // short of a free end; the face finish returns across the end (its own band below)
  const ws = wrap && (wrap.start || wrap.end) && !path.closed ? endWrapSpec(el) : null
  const lastLayer = el.assembly.layers.length - 1
  const inner = (pts: Pt2[] | null, i: number): Pt2[] | null =>
    !pts || !ws || i === 0 || i === lastLayer ? pts : (trimPolylineEnds(pts, wrap!.start, wrap!.end, ws.w) ?? pts)
  let cum = 0
  const coreStart = el.assembly.coreBoundary?.start ?? 0
  const coreEnd = el.assembly.coreBoundary?.end ?? coreStart
  for (const [i, layer] of el.assembly.layers.entries()) {
    const d0 = cum
    cum += layer.thicknessFt
    if (layer.thicknessFt <= 1e-9) continue
    const a = inner(boundaryAt(d0), i)
    const b = inner(boundaryAt(cum), i)
    if (!a || !b || a.length < 2 || b.length < 2) continue
    // annulus: outer minus inner — which offset is OUTER depends on the loop's
    // winding (a clockwise loop puts the exterior face inside), so take whichever
    // order leaves a band (2026-10-10: a CW loop's bands came back empty)
    const rings = path.closed
      ? (() => { const ab = differenceClosed([a], [b]); return ab.length ? ab : differenceClosed([b], [a]) })()
      : [[...a, ...[...b].reverse()]]                    // open run: stitched corridor
    const mid = inner(boundaryAt((d0 + cum) / 2), i) ?? []
    if (path.closed && mid.length > 1) mid.push([mid[0][0], mid[0][1]])   // loop batt runs the ring
    if (rings.length) {
      out.push({
        rings, mid, materialId: layer.materialId, depth0: d0, depth1: cum,
        isCore: i >= coreStart && i <= coreEnd, layerIndex: i,
      })
    }
  }
  if (ws) {
    const p0 = boundaryAt(ws.d0), p1 = boundaryAt(ws.d1)
    const q0 = p0 && trimPolylineEnds(p0, wrap!.start, wrap!.end, ws.w)
    const q1 = p1 && trimPolylineEnds(p1, wrap!.start, wrap!.end, ws.w)
    if (p0 && p1 && q0 && q1) {
      for (const [on, A, Aq, B, Bq] of [
        [wrap!.start, p0[0], q0[0], p1[0], q1[0]],
        [wrap!.end, p0[p0.length - 1], q0[q0.length - 1], p1[p1.length - 1], q1[q1.length - 1]],
      ] as [boolean, Pt2, Pt2, Pt2, Pt2][]) {
        if (!on) continue
        const m = (p: Pt2, q: Pt2): Pt2 => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]
        // depth0/depth1 here only carry the return's thickness (batt spacing reads the span)
        out.push({
          rings: [[A, Aq, Bq, B]], mid: [m(A, Aq), m(B, Bq)], materialId: ws.materialId,
          depth0: 0, depth1: ws.w, isCore: false, layerIndex: ws.layerIndex,
        })
      }
    }
  }
  return out.length ? out : null
}
